// Tiny typed localStorage helpers for per-device user preferences.
// These are deliberately client-only — no DB sync. Upgrade later if needed.

export type GpsPreset = "battery_saver" | "balanced" | "high_detail" | "expedition" | "custom" | "plane";

export type GpsAdaptive = {
  movingDistanceM: number;
  stoppedAfterMs: number;
  stoppedIntervalMs: number;
};

export const GPS_PRESETS: Record<"battery_saver" | "balanced" | "high_detail" | "expedition", GpsAdaptive> = {
  battery_saver: { movingDistanceM: 500,  stoppedAfterMs: 5 * 60_000,  stoppedIntervalMs: 10 * 60_000 },
  balanced:      { movingDistanceM: 75,   stoppedAfterMs: 2 * 60_000,  stoppedIntervalMs: 2 * 60_000 },
  high_detail:   { movingDistanceM: 25,   stoppedAfterMs: 30_000,      stoppedIntervalMs: 30_000 },
  // Ultra-low-power preset for multi-day hiking with limited charging.
  expedition:    { movingDistanceM: 1500, stoppedAfterMs: 10 * 60_000, stoppedIntervalMs: 30 * 60_000 },
};

export const GPS_CUSTOM_MIN_M = 3;
export const GPS_CUSTOM_MAX_M = 1000;
export const GPS_CUSTOM_DEFAULT_M = 50;
const CUSTOM_DISTANCE_KEY = "roava.gps_custom_distance_m";

export type PlanePreset = "5km" | "10km" | "25km" | "custom";
export const PLANE_CUSTOM_MIN_KM = 1;
export const PLANE_CUSTOM_MAX_KM = 200;
export const PLANE_CUSTOM_DEFAULT_KM = 10;
const PLANE_PRESET_KEY = "roava.plane_preset";
const PLANE_CUSTOM_KM_KEY = "roava.plane_custom_km";

const REMINDER_DELAY_KEY = "roava.reminder_delay_ms";
const GPS_PRESET_KEY = "roava.gps_preset";
const BATTERY_GUARD_MODE_KEY = "roava.battery_guard_mode";
const BATTERY_GUARD_THRESHOLD_KEY = "roava.battery_guard_threshold_pct";
const BATTERY_GUARD_ALLOW_CHARGING_KEY = "roava.battery_guard_allow_charging";

export const REMINDER_DELAY_DEFAULT = 2 * 60 * 60 * 1000; // 2h
export const REMINDER_DELAY_OPTIONS: Array<{ label: string; value: number }> = [
  { label: "1 hour",  value: 1 * 60 * 60 * 1000 },
  { label: "2 hours", value: 2 * 60 * 60 * 1000 },
  { label: "4 hours", value: 4 * 60 * 60 * 1000 },
  { label: "8 hours", value: 8 * 60 * 60 * 1000 },
];

export type BatteryGuardMode = "off" | "warn" | "pause";
// 50/75/95 are intentionally exposed for QA/test runs so the guard can be
// triggered without draining the device. They are real, user-selectable
// options — picking 95 means "treat anything under 95% as low".
export const BATTERY_GUARD_THRESHOLDS = [5, 10, 15, 20, 25, 50, 75, 95] as const;
export const BATTERY_GUARD_THRESHOLD_DEFAULT = 20;
export const BATTERY_GUARD_MODE_DEFAULT: BatteryGuardMode = "off";

export function getBatteryGuardMode(): BatteryGuardMode {
  const raw = safeGet(BATTERY_GUARD_MODE_KEY);
  if (raw === "off" || raw === "warn" || raw === "pause") return raw;
  return BATTERY_GUARD_MODE_DEFAULT;
}
export function setBatteryGuardMode(m: BatteryGuardMode) {
  safeSet(BATTERY_GUARD_MODE_KEY, m);
}
export function getBatteryGuardThresholdPct(): number {
  const raw = safeGet(BATTERY_GUARD_THRESHOLD_KEY);
  const n = raw ? Number(raw) : NaN;
  const allowed = BATTERY_GUARD_THRESHOLDS as readonly number[];
  if (Number.isFinite(n) && allowed.includes(n)) return n;
  return BATTERY_GUARD_THRESHOLD_DEFAULT;
}
export function setBatteryGuardThresholdPct(n: number) {
  const allowed = BATTERY_GUARD_THRESHOLDS as readonly number[];
  const v = allowed.includes(n) ? n : BATTERY_GUARD_THRESHOLD_DEFAULT;
  safeSet(BATTERY_GUARD_THRESHOLD_KEY, String(v));
}

// Tracking-specific "allow when charging" exception for the Battery Guard.
// Default true preserves the previous behavior where charging always bypassed
// the guard. When false, low battery pauses tracking even while charging.
export const TRACKING_ALLOW_WHEN_CHARGING_DEFAULT = true;
export function getTrackingAllowWhenCharging(): boolean {
  const raw = safeGet(BATTERY_GUARD_ALLOW_CHARGING_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return TRACKING_ALLOW_WHEN_CHARGING_DEFAULT;
}
export function setTrackingAllowWhenCharging(v: boolean) {
  safeSet(BATTERY_GUARD_ALLOW_CHARGING_KEY, v ? "true" : "false");
}

// Live Activity (Lock Screen / Dynamic Island tracking controls).
// Default ON for existing users — missing key is treated as enabled. Storage
// only; Settings UI is responsible for notifying the controller so this
// module stays free of cross-imports into live-activity (avoids a cycle).
const LIVE_ACTIVITY_ENABLED_KEY = "roava.liveActivity.enabled";
export const LIVE_ACTIVITY_ENABLED_DEFAULT = true;
export function getLiveActivityEnabled(): boolean {
  const raw = safeGet(LIVE_ACTIVITY_ENABLED_KEY);
  if (raw === "true") return true;
  if (raw === "false") return false;
  return LIVE_ACTIVITY_ENABLED_DEFAULT;
}
export function setLiveActivityEnabled(v: boolean) {
  safeSet(LIVE_ACTIVITY_ENABLED_KEY, v ? "true" : "false");
}


function safeGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
}

export function getReminderDelayMs(): number {
  const raw = safeGet(REMINDER_DELAY_KEY);
  const n = raw ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : REMINDER_DELAY_DEFAULT;
}
export function setReminderDelayMs(ms: number) {
  safeSet(REMINDER_DELAY_KEY, String(ms));
}

export function getGpsPreset(): GpsPreset {
  const raw = safeGet(GPS_PRESET_KEY);
  if (raw === "battery_saver" || raw === "balanced" || raw === "high_detail" || raw === "expedition" || raw === "custom" || raw === "plane") return raw;
  return "balanced";
}
export function setGpsPreset(p: GpsPreset) {
  safeSet(GPS_PRESET_KEY, p);
}

export function getGpsCustomDistanceM(): number {
  const raw = safeGet(CUSTOM_DISTANCE_KEY);
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return GPS_CUSTOM_DEFAULT_M;
  const clamped = Math.round(Math.min(GPS_CUSTOM_MAX_M, Math.max(GPS_CUSTOM_MIN_M, n)));
  return clamped;
}
export function setGpsCustomDistanceM(n: number) {
  if (!Number.isFinite(n)) return;
  const clamped = Math.round(Math.min(GPS_CUSTOM_MAX_M, Math.max(GPS_CUSTOM_MIN_M, n)));
  safeSet(CUSTOM_DISTANCE_KEY, String(clamped));
}

export function getPlanePreset(): PlanePreset {
  const raw = safeGet(PLANE_PRESET_KEY);
  if (raw === "5km" || raw === "10km" || raw === "25km" || raw === "custom") return raw;
  return "10km";
}
export function setPlanePreset(p: PlanePreset) {
  safeSet(PLANE_PRESET_KEY, p);
}
export function getPlaneCustomKm(): number {
  const raw = safeGet(PLANE_CUSTOM_KM_KEY);
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return PLANE_CUSTOM_DEFAULT_KM;
  return Math.round(Math.min(PLANE_CUSTOM_MAX_KM, Math.max(PLANE_CUSTOM_MIN_KM, n)));
}
export function setPlaneCustomKm(n: number) {
  if (!Number.isFinite(n)) return;
  const clamped = Math.round(Math.min(PLANE_CUSTOM_MAX_KM, Math.max(PLANE_CUSTOM_MIN_KM, n)));
  safeSet(PLANE_CUSTOM_KM_KEY, String(clamped));
}
function planeMovingDistanceM(): number {
  const p = getPlanePreset();
  if (p === "5km") return 5000;
  if (p === "10km") return 10000;
  if (p === "25km") return 25000;
  return getPlaneCustomKm() * 1000;
}

export function getGpsAdaptive(): GpsAdaptive {
  const preset = getGpsPreset();
  if (preset === "custom") {
    return {
      movingDistanceM: getGpsCustomDistanceM(),
      stoppedAfterMs: 2 * 60_000,
      stoppedIntervalMs: 2 * 60_000,
    };
  }
  if (preset === "plane") {
    return {
      movingDistanceM: planeMovingDistanceM(),
      stoppedAfterMs: 10 * 60_000,
      stoppedIntervalMs: 20 * 60_000,
    };
  }
  return GPS_PRESETS[preset];
}

// ────────────────────────────────────────────────────────────────────────────
// Pod grouping sensitivity (gallery clustering)
// Controls how the Library groups photos into "Pods" — a Pod ends when you've
// either moved farther than `radiusM` from the cluster or no new photos have
// arrived for `gapMs`. Device-local; no server sync.
// ────────────────────────────────────────────────────────────────────────────

export type PodPreset = "tight" | "standard" | "loose" | "custom";

const POD_PRESET_KEY = "roava.pod_preset";
const POD_CUSTOM_RADIUS_KEY = "roava.pod_custom_radius_m";
const POD_CUSTOM_GAP_KEY = "roava.pod_custom_gap_min";

export const POD_CUSTOM_RADIUS_MIN_M = 25;
export const POD_CUSTOM_RADIUS_MAX_M = 2000;
export const POD_CUSTOM_RADIUS_DEFAULT_M = 200;

export const POD_CUSTOM_GAP_MIN_MIN = 5;
export const POD_CUSTOM_GAP_MAX_MIN = 240;
export const POD_CUSTOM_GAP_DEFAULT_MIN = 45;

export const POD_PRESETS: Record<"tight" | "standard" | "loose", { radiusM: number; gapMin: number }> = {
  tight:    { radiusM: 75,  gapMin: 20 },
  standard: { radiusM: 200, gapMin: 45 },
  loose:    { radiusM: 500, gapMin: 90 },
};

/** Event name fired (window) when pod grouping prefs change. */
export const POD_PREFS_CHANGED_EVENT = "roava:pod-prefs-changed";

function emitPodChanged() {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new Event(POD_PREFS_CHANGED_EVENT)); } catch { /* ignore */ }
}

export function getPodPreset(): PodPreset {
  const raw = safeGet(POD_PRESET_KEY);
  if (raw === "tight" || raw === "standard" || raw === "loose" || raw === "custom") return raw;
  return "standard";
}
export function setPodPreset(p: PodPreset) {
  safeSet(POD_PRESET_KEY, p);
  emitPodChanged();
}

export function getPodCustomRadiusM(): number {
  const raw = safeGet(POD_CUSTOM_RADIUS_KEY);
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return POD_CUSTOM_RADIUS_DEFAULT_M;
  return Math.round(Math.min(POD_CUSTOM_RADIUS_MAX_M, Math.max(POD_CUSTOM_RADIUS_MIN_M, n)));
}
export function setPodCustomRadiusM(n: number) {
  if (!Number.isFinite(n)) return;
  const v = Math.round(Math.min(POD_CUSTOM_RADIUS_MAX_M, Math.max(POD_CUSTOM_RADIUS_MIN_M, n)));
  safeSet(POD_CUSTOM_RADIUS_KEY, String(v));
  emitPodChanged();
}

export function getPodCustomGapMin(): number {
  const raw = safeGet(POD_CUSTOM_GAP_KEY);
  const n = raw ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return POD_CUSTOM_GAP_DEFAULT_MIN;
  return Math.round(Math.min(POD_CUSTOM_GAP_MAX_MIN, Math.max(POD_CUSTOM_GAP_MIN_MIN, n)));
}
export function setPodCustomGapMin(n: number) {
  if (!Number.isFinite(n)) return;
  const v = Math.round(Math.min(POD_CUSTOM_GAP_MAX_MIN, Math.max(POD_CUSTOM_GAP_MIN_MIN, n)));
  safeSet(POD_CUSTOM_GAP_KEY, String(v));
  emitPodChanged();
}

/** Resolve the active grouping thresholds used by the gallery clusterer. */
export function getPodGrouping(): { radiusM: number; gapMs: number; preset: PodPreset } {
  const preset = getPodPreset();
  if (preset === "custom") {
    return {
      radiusM: getPodCustomRadiusM(),
      gapMs: getPodCustomGapMin() * 60_000,
      preset,
    };
  }
  const p = POD_PRESETS[preset];
  return { radiusM: p.radiusM, gapMs: p.gapMin * 60_000, preset };
}

